import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Button from '@components/Button';
import FiltersModal from '@components/FiltersModal';
import { FiltersFormCreator } from '@components/FiltersFormCreator';
import { FiltersIcon } from 'assets/images/icons';
import { useTranslation } from '@hooks';
import { TEMPLATE_CHANNEL_FILTER, EMPTY_TEMPLATE_FILTER } from '@constants/campaignTemplates';

const CHANNEL_TYPE_LABEL_KEYS = {
    [TEMPLATE_CHANNEL_FILTER.EMAIL]: 'TemplateLibrary.channelEmail',
    [TEMPLATE_CHANNEL_FILTER.WHATSAPP]: 'TemplateLibrary.channelWhatsApp',
};

const FiltersPanel = ({ filter, setFilter, channelTypes }) => {
    const { t } = useTranslation();
    const [filterOpened, setFiltersOpened] = useState(false);
    // The modal edits a draft; only Apply commits it to the live filter — Cancel/close discards.
    const [draft, setDraft] = useState(filter);

    const brands = useSelector(state => state.brands.data);

    const filterNumber = Number(filter.channelTypes.length > 0) + Number(filter.brandIds.length > 0);

    const openModal = () => {
        setDraft(filter);
        setFiltersOpened(true);
    };

    const options = useMemo(
        () => ({
            channelTypes: (channelTypes || [])
                .filter(type => CHANNEL_TYPE_LABEL_KEYS[type])
                .map(type => ({
                    value: type,
                    label: t(CHANNEL_TYPE_LABEL_KEYS[type]),
                    name: t(CHANNEL_TYPE_LABEL_KEYS[type]),
                })),
            brands: (brands || []).map(brand => ({ value: brand.id, label: brand.title, name: brand.title })),
        }),
        [brands, channelTypes, t],
    );

    const formOptions = [
        ...(options.channelTypes.length > 1
            ? [
                  {
                      type: 'multiSelect',
                      label: t('TemplateLibrary.filterChannelType'),
                      value: draft.channelTypes,
                      onChange: value => setDraft(prev => ({ ...prev, channelTypes: value })),
                      options: options.channelTypes,
                      countable: true,
                      nameBased: true,
                      style: { gridColumn: '1 / -1' },
                  },
              ]
            : []),
        ...(options.brands.length > 0
            ? [
                  {
                      type: 'multiSelect',
                      label: t('TemplateLibrary.filterBrands'),
                      value: draft.brandIds,
                      onChange: value => setDraft(prev => ({ ...prev, brandIds: value })),
                      options: options.brands,
                      countable: true,
                      nameBased: true,
                      style: { gridColumn: '1 / -1' },
                  },
              ]
            : []),
    ];

    if (formOptions.length === 0) return null;

    return (
        <>
            <Button variant="outlined" onClick={openModal} startIcon={<FiltersIcon />}>
                {t('buttons.filters')}
                {filterNumber > 0 ? ` (${filterNumber})` : ''}
            </Button>
            <FiltersModal
                isOpen={filterOpened}
                handleClose={() => setFiltersOpened(false)}
                onApply={() => {
                    setFilter(draft);
                    setFiltersOpened(false);
                }}
                onClear={() => setDraft(EMPTY_TEMPLATE_FILTER)}
                clearDisabled={draft.channelTypes.length === 0 && draft.brandIds.length === 0}
            >
                <FiltersFormCreator type="template-library" options={formOptions} />
            </FiltersModal>
        </>
    );
};

export default FiltersPanel;
